import { ReactNode, useCallback, useEffect, useRef, useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import { adminAPI, analyticsAPI, paperAPI, signalsAPI, trackRecordAPI } from '../services/api'
import { useAuthStore } from '../store/authStore'
import type {
  AnalyticsOverview,
  PaperAccountSummary,
  PortfolioHeat,
  SignalTrackRecordSummary,
} from '../types'
import LivePriceChart from '../components/LivePriceChart'
import LoadingScreen from '../components/LoadingScreen'
import PortfolioHeatGauge from '../components/PortfolioHeatGauge'
import ScanHeartbeat from '../components/ScanHeartbeat'
import TickerTape from '../components/TickerTape'
import NewsTicker, { NEWS_TICKER_ENABLED } from '../components/NewsTicker'
import Tooltip from '../components/Tooltip'
import EngineActivityFeed from '../components/dashboard/EngineActivityFeed'
import EngineStandDownBanner from '../components/dashboard/EngineStandDownBanner'
import { standDownStreak, useEngineActivity } from '../components/dashboard/engineActivity'
import OpenPositionsPanel from '../components/dashboard/OpenPositionsPanel'
import PaperActivityFeed from '../components/dashboard/PaperActivityFeed'
import PaperEquityChart from '../components/dashboard/PaperEquityChart'
import PaperPositionsPanel from '../components/dashboard/PaperPositionsPanel'
import PaperRegimeBadge from '../components/dashboard/PaperRegimeBadge'
import { TONES, Tone } from '../components/dashboard/TerminalPanel'
import TrackRecordSnapshot from '../components/dashboard/TrackRecordSnapshot'
import { DASHBOARD_GLOSSARY, SIGNALS_GLOSSARY } from '../config/glossary'
import TerminalCanvas from '../theme/TerminalCanvas'
import { HeatLevel, formatHeatPct, heatLevel } from '../utils/portfolioHeat'
import { MIN_WIN_RATE_SAMPLE, formatWinRate, hasWinRate, pendingWinRateNote } from '../utils/winRate'

type DashboardView = 'real' | 'paper'

// Matches the scan heartbeat's poll, so the open count it shows stays current.
const SUMMARY_POLL_MS = 60_000

// "real" is the signal engine's live view: its hourly scan and its own record.
const VIEW_LABELS: Record<DashboardView, string> = {
  real: 'Signal engine',
  paper: 'Paper',
}

const money = (value: number) =>
  value.toLocaleString('en-US', { style: 'currency', currency: 'USD', minimumFractionDigits: 2, maximumFractionDigits: 2 })

const Dashboard = () => {
  const [searchParams, setSearchParams] = useSearchParams()
  const requested = searchParams.get('view')
  // Anything but 'paper' is the signal engine, including links to the
  // retired 'demo' view.
  const view: DashboardView = requested === 'paper' ? 'paper' : 'real'
  const [overview, setOverview] = useState<AnalyticsOverview | null>(null)
  const [heat, setHeat] = useState<PortfolioHeat | null>(null)
  const [summary, setSummary] = useState<SignalTrackRecordSummary | null>(null)
  const [paper, setPaper] = useState<PaperAccountSummary | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(false)
  // Drops responses from a load superseded by a newer one (e.g. a view switch).
  const latestLoad = useRef(0)
  const isAdmin = useAuthStore((state) => state.user?.is_superuser === true)
  // One poll feeds both the stand-down banner and the activity feed.
  const activity = useEngineActivity(view !== 'paper')
  const streak = activity.events ? standDownStreak(activity.events) : null
  // The open-position cap is served only in the admin diagnostics facts.
  const [maxOpen, setMaxOpen] = useState<number | null>(null)
  const needsCap = isAdmin && streak?.reason === 'max_open_platform_signals'
  const streakKey = streak ? `${streak.reason}@${streak.lastScan.getTime()}` : null

  const loadData = useCallback(
    async () => {
      const loadId = ++latestLoad.current
      setLoading(true)
      setError(false)
      try {
        const isPaperView = view === 'paper'
        // Gross, as everything a user sees: no fee is passed.
        const [overviewData, heatData, summaryData, paperData] = await Promise.all([
          analyticsAPI.overview(),
          // Tile-level data: a failure blanks its tile, not the whole dashboard.
          isPaperView ? null : signalsAPI.portfolioHeat().catch(() => null),
          trackRecordAPI.getSignalSummary(false).catch(() => null),
          // The paper account is its own ledger, never folded into the others.
          isPaperView ? paperAPI.account().catch(() => null) : null,
        ])
        if (loadId !== latestLoad.current) return

        setOverview(overviewData)
        setHeat(heatData)
        setSummary(summaryData)
        setPaper(paperData)
      } catch (error) {
        if (loadId !== latestLoad.current) return
        console.error('Error loading dashboard data:', error)
        setError(true)
      } finally {
        if (loadId === latestLoad.current) setLoading(false)
      }
    },
    [view]
  )

  useEffect(() => {
    loadData()
  }, [loadData])

  useEffect(() => {
    if (!needsCap) return
    let active = true
    adminAPI
      .diagnosticsFacts()
      .then((facts) => {
        if (active) setMaxOpen(facts.open_book.max_open)
      })
      .catch(() => {
        // The banner then gives the open count without the cap.
      })
    return () => {
      active = false
    }
  }, [needsCap, streakKey])

  // Keeps the open count, win rate and closed count live between full loads.
  // Only the summary refreshes, and a failed poll keeps the last reading.
  useEffect(() => {
    if (view === 'paper') return
    let cancelled = false
    const timer = setInterval(async () => {
      try {
        const next = await trackRecordAPI.getSignalSummary(false)
        if (!cancelled) setSummary(next)
      } catch (error) {
        console.error('Signal summary refresh failed:', error)
      }
    }, SUMMARY_POLL_MS)
    return () => {
      cancelled = true
      clearInterval(timer)
    }
  }, [view])

  const selectView = (next: DashboardView) => {
    if (next === view) return
    setSearchParams(
      (prev) => {
        const params = new URLSearchParams(prev)
        if (next === 'real') params.delete('view')
        else params.set('view', next)
        return params
      },
      { replace: true }
    )
  }

  if (loading) {
    return (
      <TerminalCanvas>
        <TickerTape />
        <LoadingScreen />
      </TerminalCanvas>
    )
  }

  if (error || !overview) {
    return (
      <TerminalCanvas>
        <TickerTape />
        <div className="flex flex-col items-center justify-center py-24 space-y-4">
          <p className="text-gray-400">Failed to load dashboard data.</p>
          <button
            onClick={() => loadData()}
            className="px-4 py-2 bg-primary-600 text-white rounded-md hover:bg-primary-700"
          >
            Retry
          </button>
        </div>
      </TerminalCanvas>
    )
  }

  // Win Rate and Closed Signals come from the polled summary, the same numbers
  // as the Track Record panel and "usable X / 200", so a close that lands
  // while the page is open moves them all together. The overview, loaded
  // once, is the fallback when the summary fails.
  const closedSignals = summary ? summary.closed_signals : overview.closed_signals
  const winRate = summary ? summary.win_rate : overview.overall_win_rate
  const showsWinRate = hasWinRate(winRate, closedSignals)
  const phase2 = summary?.phase2_progress
  const isPaper = view === 'paper'
  // The one open-signal count on the page: the status line and the Open
  // Signals tile both show this, so they can never disagree.
  const openSignals = summary?.open_signals ?? overview.active_signals

  return (
    <TerminalCanvas>
      <TickerTape />
      <div className="space-y-6">
        <div className="space-y-1">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <h1 className="text-3xl font-bold text-gray-50">Dashboard</h1>
            <div
              role="group"
              aria-label="Track record view"
              className="inline-flex rounded-md border border-terminal-border bg-terminal-surface p-0.5"
            >
              {(['real', 'paper'] as const).map((option) => (
                <button
                  key={option}
                  type="button"
                  aria-pressed={view === option}
                  onClick={() => selectView(option)}
                  className={`px-3 py-1 text-sm font-medium rounded ${
                    view === option
                      ? option === 'paper'
                        ? 'bg-violet-500 text-gray-950'
                        : 'bg-primary-600 text-white'
                      : 'text-gray-400 hover:bg-white/5 hover:text-gray-200'
                  }`}
                >
                  {VIEW_LABELS[option]}
                </button>
              ))}
            </div>
          </div>
          {/* The heartbeat reports the signal engine's scan; the paper engine has
              its own activity feed and daily cadence. */}
          {!isPaper && <ScanHeartbeat openSignals={openSignals} />}
        </div>

        {!isPaper && streak && (
          <EngineStandDownBanner
            streak={streak}
            facts={{
              openCount: summary?.open_signals ?? null,
              maxOpen: needsCap ? maxOpen : null,
              heatPct: summary?.portfolio_heat_pct ?? null,
              maxHeatPct: summary?.max_portfolio_heat_pct ?? null,
            }}
          />
        )}

        {isPaper && (
          <div
            className="rounded-lg border border-violet-500/40 bg-violet-500/10 px-4 py-3 text-sm text-violet-200"
            data-testid="paper-banner"
          >
            Paper view — pretend money on real prices. Never counts toward the real record.
          </div>
        )}

        {/* Stats Grid - live signal metrics */}
        {isPaper ? (
          <div className="space-y-3">
            <PaperRegimeBadge />
            <PaperTiles account={paper} />
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            <StatCard
              title="Open Signals"
              tone="sky"
              value={openSignals}
              subtitle={
                summary ? `${summary.open_long} long · ${summary.open_short} short` : 'Long/short split unavailable'
              }
              subtitleHint={summary ? DASHBOARD_GLOSSARY.directionSplit(summary.open_long, summary.open_short) : undefined}
            />
            <StatCard
              title="Portfolio Heat"
              tone={heat ? HEAT_TONES[heatLevel(heat.total_heat_pct, heat.max_heat_pct)] : 'slate'}
              hint={SIGNALS_GLOSSARY.portfolioHeat(heat ? formatHeatPct(heat.max_heat_pct, 0) : undefined)}
            >
              {heat ? (
                <PortfolioHeatGauge heatPct={heat.total_heat_pct} maxHeatPct={heat.max_heat_pct} />
              ) : (
                <>
                  <p className="text-3xl font-bold text-gray-50 mt-2">—</p>
                  <p className="text-sm text-gray-400 mt-1">Unavailable</p>
                </>
              )}
            </StatCard>
            <StatCard
              title="Win Rate"
              tone={showsWinRate ? 'green' : 'slate'}
              value={formatWinRate(winRate, closedSignals)}
              valueHint={
                showsWinRate
                  ? undefined
                  : closedSignals > 0
                  ? DASHBOARD_GLOSSARY.winRateSmallSample(closedSignals, MIN_WIN_RATE_SAMPLE)
                  : DASHBOARD_GLOSSARY.winRateEmpty
              }
              subtitle={
                showsWinRate
                  ? `Across ${closedSignals} closed signal${closedSignals === 1 ? '' : 's'} · before fees`
                  : pendingWinRateNote(closedSignals, 'signal')
              }
            />
            <StatCard
              title="Closed Signals"
              tone="indigo"
              value={closedSignals}
              subtitle={
                phase2
                  ? phase2.reached
                    ? 'Enough closes for Phase 2 to train'
                    : `usable ${phase2.usable_rows} / ${phase2.threshold} toward Phase 2`
                  : `of ${overview.total_signals} total`
              }
              subtitleHint={phase2 && !phase2.reached ? DASHBOARD_GLOSSARY.phase2Progress(phase2.threshold) : undefined}
            />
          </div>
        )}

        {/* Live market chart - real venue prices, never performance. The paper
            view swaps in its own equity curve: there, performance is the point. */}
        {isPaper ? <PaperEquityChart account={paper} /> : <LivePriceChart />}

        {/* The engine at work: real signals only, so the paper view swaps in
            its own strategy's trades. */}
        {isPaper ? (
          <div className="grid grid-cols-1 gap-6 xl:grid-cols-5">
            <div className="xl:col-span-3">
              <PaperPositionsPanel />
            </div>
            <div className="xl:col-span-2">
              <PaperActivityFeed />
            </div>
          </div>
        ) : (
          <>
            {/* Side by side from xl: below it the 3/5 column is too narrow for
                a position row's coin, badge and three fixed columns. */}
            <div className="grid grid-cols-1 gap-6 xl:grid-cols-5">
              <div className="xl:col-span-3">
                <OpenPositionsPanel />
              </div>
              <div className="xl:col-span-2">
                <EngineActivityFeed activity={activity} />
              </div>
            </div>
            <TrackRecordSnapshot summary={summary} />
          </>
        )}
      </div>
      {NEWS_TICKER_ENABLED && <NewsTicker />}
    </TerminalCanvas>
  )
}

/**
 * The Paper account's four tiles. Every figure here is pretend money measured
 * on real prices, and none of it touches the real record.
 */
const PaperTiles = ({ account }: { account: PaperAccountSummary | null }) => {
  if (!account) {
    return (
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        {['Starting balance', 'Current balance', 'Profit / loss', 'Win rate'].map((title) => (
          <StatCard key={title} title={title} tone="slate" value="—" subtitle="Paper account unavailable" />
        ))}
      </div>
    )
  }

  const notStarted = account.status === 'not_started'
  const up = account.profit_loss > 0
  const down = account.profit_loss < 0
  const sign = up ? '+' : ''
  const wl = `${account.wins}W / ${account.losses}L`

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
      <StatCard
        title="Starting balance"
        tone="slate"
        value={money(account.starting_balance)}
        valueHint="Pretend money. The paper account has never held anything real."
        subtitle="pretend"
      />
      <StatCard
        title="Current balance"
        tone="violet"
        value={money(account.current_balance)}
        subtitle={notStarted ? 'starts at 00:15 UTC' : `day ${account.day} of ~${account.eval_days}`}
        subtitleHint={
          notStarted
            ? `The first scheduled run starts the clock and fixes the evaluation date, ${account.eval_days} days later.`
            : `Forward test launched ${account.launched_on}, evaluated after ${account.eval_days} days.`
        }
      />
      <StatCard title="Profit / loss" tone={up ? 'green' : down ? 'red' : 'slate'}>
        <p
          className={`text-3xl font-bold mt-2 tabular-nums ${
            up ? 'text-green-400' : down ? 'text-red-400' : 'text-gray-50'
          }`}
          data-testid="paper-pnl"
        >
          {sign}
          {money(account.profit_loss)}
        </p>
        <p className={`text-sm mt-1 tabular-nums ${up ? 'text-green-400' : down ? 'text-red-400' : 'text-gray-400'}`}>
          {sign}
          {account.profit_loss_pct.toFixed(2)}%
        </p>
      </StatCard>
      <StatCard
        title="Win rate"
        tone={account.win_rate != null ? 'green' : 'slate'}
        value={account.win_rate != null ? `${account.win_rate.toFixed(1)}%` : '—'}
        valueHint={
          account.win_rate != null
            ? 'Closed paper trades only. The strategy is built to win less than half its trades and hold the winners.'
            : 'No paper trade has closed yet.'
        }
        subtitle={
          account.closed_trades > 0
            ? `${wl} · ${account.closed_trades} closed`
            : notStarted
            ? 'no trades yet'
            : `${account.open_positions} open · none closed yet`
        }
      />
    </div>
  )
}

// Heat uses its status bands: green healthy, amber warming, red near/over limit.
const HEAT_TONES: Record<HeatLevel, Tone> = {
  healthy: 'green',
  warming: 'amber',
  near_limit: 'red',
  over_limit: 'red',
}

interface StatCardProps {
  title: string
  /** Accent colour of the tile's top rule and glow. */
  tone: Tone
  /** Explains the title, as a tooltip on it. */
  hint?: string
  value?: string | number
  /** Explains the value, as a tooltip on it. */
  valueHint?: string
  subtitle?: string
  /** Explains the subtitle, as a tooltip on it. */
  subtitleHint?: string
  /** Custom body in place of value + subtitle. */
  children?: ReactNode
}

// `className` reaches the Tooltip's trigger button, which does not inherit
// text-transform from its parent.
const withHint = (text: ReactNode, hint?: string, className?: string) =>
  hint ? (
    <Tooltip content={hint} className={className}>
      {text}
    </Tooltip>
  ) : (
    text
  )

const StatCard = ({ title, tone, hint, value, valueHint, subtitle, subtitleHint, children }: StatCardProps) => {
  const accent = TONES[tone]
  return (
    <section
      className="relative overflow-hidden rounded-lg border border-terminal-border bg-terminal-surface p-6"
      aria-label={title}
      data-tone={tone}
    >
      <span aria-hidden="true" className={`pointer-events-none absolute inset-x-0 top-0 h-1 bg-gradient-to-r ${accent.bar}`} />
      <span
        aria-hidden="true"
        className={`pointer-events-none absolute inset-0 bg-gradient-to-b ${accent.glow} to-transparent`}
      />
      <div className="relative">
        <p className="text-xs font-medium uppercase tracking-wider text-gray-400">{withHint(title, hint, 'uppercase')}</p>
        {children ?? (
          <>
            <p className="text-3xl font-bold text-gray-50 mt-2 tabular-nums">{withHint(value, valueHint)}</p>
            <p className="text-sm text-gray-400 mt-1">{withHint(subtitle, subtitleHint)}</p>
          </>
        )}
      </div>
    </section>
  )
}

export default Dashboard
